/**
 * Maps a Godot `Node2D` transform into three.js: `<group>` props for a local one, and a
 * `Matrix4` both ways for any `Transform2D`. Godot 2D is `+Y` down with clockwise
 * rotation, and three.js is `+Y` up, so each transform is conjugated by
 * `F = diag(1, -1, 1)`: Y translation and rotation negate, scale stays. One pixel is
 * one world unit, in the z=0 plane.
 */
import * as THREE from 'three';
import { transform2DFromParts } from '../godot/math/transform2d.js';
export function node2dGroupProps(t, z = 0) {
    // `0 - v` (not `-v`) so a zero input stays +0, never -0.
    const position = [t.position.x, 0 - t.position.y, z];
    const rotation = [0, 0, 0 - t.rotation];
    const scale = [t.scale.x, t.scale.y, 1];
    const skew = t.skew ?? 0;
    if (skew === 0)
        return { position, rotation, scale };
    const matrix = threeMatrixFromTransform2D(transform2DFromParts(t.rotation, t.scale, skew, t.position), z);
    return { position, rotation, scale, matrix };
}
/**
 * Writes a Godot canvas point `(x, y)` at `offset` as the three point `(x, -y, 0)` of a Node2D
 * group's local space. A gizmo that builds line segments in a Float32Array calls it for each vertex.
 * `0 - y`, not `-y`, keeps a zero y at +0.
 */
export function writeGodotPointAsThree(out, offset, x, y) {
    out[offset] = x;
    out[offset + 1] = 0 - y;
    out[offset + 2] = 0;
}
/**
 * A Node2D's local `Transform2D`, each absent part at its `node_2d.h:39-42` default: position
 * `(0, 0)`, rotation 0, scale `(1, 1)` and skew 0. A hand-built bag may skip the parser that
 * fills them.
 */
export function node2DLocalTransform(parts) {
    return transform2DFromParts(parts.rotation ?? 0, parts.scale ?? { x: 1, y: 1 }, parts.skew ?? 0, parts.position ?? { x: 0, y: 0 });
}
/**
 * `t` conjugated by F at depth `z`: Godot's `columns[0] = (a, b)`, `columns[1] = (c, d)` and
 * `columns[2] = (tx, ty)` become `[a, -c, tx; -b, d, -ty]`. Baked whole, so a shear survives.
 */
export function threeMatrixFromTransform2D(t, z = 0) {
    return new THREE.Matrix4().set(t.a, -t.c, 0, t.tx, 
    // `0 - ty` (not `-ty`) so a zero origin stays +0, as in `node2dGroupProps`.
    -t.b, t.d, 0, 0 - t.ty, 0, 0, 1, z, 0, 0, 0, 1);
}
/**
 * The Godot `Transform2D` behind a matrix in the conjugated space, the inverse of
 * {@link threeMatrixFromTransform2D}: F is its own inverse, so the 2x2's off-diagonal terms flip
 * sign and the Y translation negates.
 */
export function transform2DFromThreeMatrix(matrix) {
    const e = matrix.elements;
    return {
        a: e[0],
        b: 0 - e[1],
        c: 0 - e[4],
        d: e[5],
        tx: e[12],
        ty: 0 - e[13],
    };
}
/**
 * The same local transform as a `Matrix4`, for a consumer composing this item's
 * space with another. Re-deriving it from the discrete props gives a different
 * answer for a sheared item.
 */
export function node2dGroupMatrix(g) {
    if (g.matrix)
        return g.matrix.clone();
    return new THREE.Matrix4().compose(new THREE.Vector3(...g.position), new THREE.Quaternion().setFromEuler(new THREE.Euler(...g.rotation)), new THREE.Vector3(...g.scale));
}
/**
 * Spreadable `<group>` transform props: discrete position/rotation/scale for the
 * common (skew-free) case, or a baked `matrix` with `matrixAutoUpdate=false`
 * when a shear is present (the matrix would otherwise be overwritten from the
 * group's identity position/quaternion/scale each frame).
 */
export function node2dGroupSpread(g) {
    return g.matrix
        ? { matrix: g.matrix, matrixAutoUpdate: false }
        : { position: g.position, rotation: g.rotation, scale: g.scale };
}
//# sourceMappingURL=node2dTransform.js.map